import React from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import DialogContent from "@material-ui/core/DialogContent";
import CustomDialog from "../Shared/Components/CustomDialog";
import PropTypes from "prop-types";
import CheckIcon from "@material-ui/icons/Check";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { checkArtusRule, checkBertWarehouseRule } from "../Rules";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  MenuItemOption: {
    "@media (hover: none) , (pointer: coarse)": {
      "&:focus": {
        backgroundColor: "#fff",
      },
    },
  },
  select: {
    backgroundColor: "#fff",
    minWidth: 200,
    [theme.breakpoints.down("xs")]: {
      minWidth: 160,
    },
  },
  controlLabel: {
    paddingRight: 10,
  },
  dialogContentRoot: {
    paddingBottom: 20,
    "& > label": { padding: "20px 0" },
  },
  toolTitle: {
    alignSelf: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dialogRoot: {
    "& .MuiBackdrop-root": {
      opacity: "0 !important",
    },
    "& .MuiPaper-root > div": {
      backgroundColor: "#e8e9eb",
    },
  },
  checkBox: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
    width: 25,
    height: 25,
    marginLeft: 20,
    cursor: "pointer",
    borderRadius: 4,
    backgroundColor: "#fff",
  },
  disabledColor: {
    opacity: 0.3,
    pointerEvents: "none",
    cursor: "default",
  },
  root: {
    display: "flex",
    margin: "15px 0 0",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  label: {
    fontWeight: "bold",
    textAlign: "left",
    verticalAlign: "middle",
    wordBreak: "break-word",
    whiteSpace: "normal",
    width: "30%",
    [theme.breakpoints.down("xs")]: {
      width: "90%",
    },
    display: "flex",
    alignItems: "center",
  },
  dropdown: {
    display: "inline-flex",
    width: "60%",
    minWidth: 160,
    marginLeft: 20,
    float: "right",
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
    },
  },
  checkboxRoot: {
    marginBottom: "auto",
    padding: "0 12px",
  },
}));

const FieldOptionsDialog = (props) => {
  const {
    isLeftOuterField,
    isRightOuterField,
    leftBullBar,
    rightBullBar,
    show,
    updateHeight,
    updateWidth,
    updateShelfLoad,
    updateSupportBarsAmount,
    updateLeftBullBar,
    updateRightBullBar,
    supportBars,
    onClose,
    height,
    width,
    shelfLoad,
    securing,
    updateSecuring,
    leftSidePanel,
    updateLeftSidePanel,
    rightSidePanel,
    updateRightSidePanel,
    dialogPosition,
  } = props;

  const classes = useStyles();
  const { t } = useTranslation(["fieldOptionsDialog", "common"]);
  const {
    configuration,
    artusComponentOptions,
    bertWarehouseComponentOptions,
    bertArchiveComponentOptions,
  } = useSelector((state) => {
    return state.Configuration;
  });
  const bullBarOptions = artusComponentOptions.find((opt) => opt.key === "bullbar").options;
  const getTitle = (identifier) => {
    const option = bullBarOptions.find((option) => option.identifier.includes(identifier));
    return option ? option.title : "";
  };
  const itemIdentifier = configuration && configuration.item ? configuration.item.identifier : "";
  const isBertwarehouse = itemIdentifier.includes("bertWarehouse");
  const { depth } = useSelector((state) => state.BertWarehouse);
  const isBertArchive = itemIdentifier.includes("bertArchive");
  const data = isBertwarehouse
    ? [
        {
          key: "width",
          title: t("common:width"),
          selected: width,
          function: (e) => updateWidth(e.target.value),
          options: bertWarehouseComponentOptions.find((opt) => opt.key === "width").options || [],
        },
        {
          key: "row",
          title: t("common:layer"),
          selected: supportBars,
          function: (e) => updateSupportBarsAmount(e.target.value),
          options: bertWarehouseComponentOptions.find((opt) => opt.key === "row").options || [],
        },
        {
          type: "checkbox",
          key: "leftSidePanel",
          title: t("sidePanelLeft"),
          selected: leftSidePanel,
          function: (value) => {
            updateLeftSidePanel(value);
          },
        },
        {
          type: "checkbox",
          key: "rightSidePanel",
          title: t("sidePanelRight"),
          selected: rightSidePanel,
          function: (value) => updateRightSidePanel(value),
        },
      ]
    : isBertArchive
    ? [
        {
          key: "width",
          title: t("common:width"),
          selected: width,
          function: (e) => updateWidth(e.target.value),
          options: bertArchiveComponentOptions.find((opt) => opt.key === "width").options || [],
        },
        {
          key: "row",
          title: t("common:layer"),
          selected: supportBars,
          function: (e) => updateSupportBarsAmount(e.target.value),
          options: bertArchiveComponentOptions.find((opt) => opt.key === "row").options || [],
        },
        {
          type: "checkbox",
          key: "leftSidePanel",
          title: t("sidePanelLeft"),
          selected: leftSidePanel,
          function: (value) => {
            updateLeftSidePanel(value);
          },
        },
        {
          type: "checkbox",
          key: "rightSidePanel",
          title: t("sidePanelRight"),
          selected: rightSidePanel,
          function: (value) => updateRightSidePanel(value),
        },
      ]
    : [
        {
          key: "height",
          title: t("common:height"),
          selected: height,
          function: (e) => updateHeight(e.target.value),
          options: artusComponentOptions.find((opt) => opt.key === "height").options,
        },
        {
          key: "row",
          title: t("common:layer"),
          selected: supportBars,
          function: (e) => updateSupportBarsAmount(e.target.value),
          options: artusComponentOptions.find((opt) => opt.key === "row").options,
        },
        {
          key: "width",
          title: t("common:width"),
          selected: width,
          function: (e) => updateWidth(e.target.value),
          options: artusComponentOptions.find((opt) => opt.key === "width").options,
        },
        {
          key: "weight",
          title: t("common:shelfLoad"),
          selected: shelfLoad,
          function: (e) => updateShelfLoad(e.target.value),
          options: artusComponentOptions.find((opt) => opt.key === "weight").options,
        },
        {
          key: "sicherung",
          title: t("securing"),
          function: (e) => {
            updateSecuring(e.target.value);
          },
          selected: securing,
          options: artusComponentOptions.find((opt) => opt.key === "sicherung").options,
        },
        {
          key: "bullbar_left",
          title: t("bullbarLeft"),
          function: (e) => {
            updateLeftBullBar(e.target.value);
          },
          selected: leftBullBar,
          options: isLeftOuterField
            ? [
                {
                  title: getTitle("frontend_noBullBar"),
                  identifier: "no_bullbar",
                },
                {
                  title: getTitle("frontend_guardrail"),
                  identifier: "railbar",
                },
                {
                  title: getTitle("frontend_singleLForm"),
                  identifier: "single_lform",
                },
                {
                  title: getTitle("frontend_doubleLForm"),
                  identifier: "double_lform",
                },
              ]
            : [
                {
                  title: getTitle("frontend_noBullBar"),
                  identifier: "no_bullbar",
                },
                {
                  title: getTitle("frontend_singleUForm"),
                  identifier: "single_uform",
                },
                {
                  title: getTitle("frontend_doubleUForm"),
                  identifier: "double_uform",
                },
              ],
        },
        {
          key: "bullbar_right",
          title: t("bullbarRight"),
          function: (e) => {
            updateRightBullBar(e.target.value);
          },
          selected: rightBullBar,
          options: isRightOuterField
            ? [
                {
                  title: getTitle("frontend_noBullBar"),
                  identifier: "no_bullbar",
                },
                {
                  title: getTitle("frontend_guardrail"),
                  identifier: "railbar",
                },
                {
                  title: getTitle("frontend_singleLForm"),
                  identifier: "single_lform",
                },
                {
                  title: getTitle("frontend_doubleLForm"),
                  identifier: "double_lform",
                },
              ]
            : [
                {
                  title: getTitle("frontend_noBullBar"),
                  identifier: "no_bullbar",
                },
                {
                  title: getTitle("frontend_singleUForm"),
                  identifier: "single_uform",
                },
                {
                  title: getTitle("frontend_doubleUForm"),
                  identifier: "double_uform",
                },
              ],
        },
      ];
  return (
    <CustomDialog
      open={show}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      dialogTitle={t("fieldOptions")}
      onCancel={onClose}
      maxWidth="xs"
      className={classes.dialogRoot}
      // PaperProps={{
      //   style: {
      //     left: dialogPosition.left - 220,
      //     bottom: dialogPosition.bottom + 30,
      //     position: "fixed",
      //     margin: 8,
      //   },
      // }}
    >
      <DialogContent className={classes.dialogContentRoot}>
        {data.map((item, i) => (
          <div className={classes.root}>
            <Typography className={classes.label} variant={"body2"}>
              {item.title}{" "}
            </Typography>
            {item.type === "checkbox" ? (
              <div
                className={
                  isBertwarehouse
                    ? clsx(classes.checkBox, depth === "frontend_depth800" && classes.disabledColor)
                    : classes.checkBox
                }
                onClick={(e) => {
                  item.function(!item.selected);
                }}
              >
                {item.selected && <CheckIcon className={classes.checkIcon} />}
              </div>
            ) : (
              <div className={classes.dropdown}>
                <Select
                  classes={{ root: classes.select }}
                  variant="outlined"
                  value={item.selected}
                  onChange={item.function}
                >
                  {item.options &&
                    item.options.map((option, i) => {
                      const isDisabled =
                        isBertwarehouse || isBertArchive
                          ? false
                          : checkArtusRule(option.identifier, height, supportBars, width);
                      return (
                        <MenuItem
                          className={classes.MenuItemOption}
                          key={option.title + i}
                          value={option.identifier}
                          disabled={isDisabled}
                        >
                          <Typography className={classes.toolTitle} noWrap>
                            {option.title}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                </Select>
              </div>
            )}
          </div>
        ))}
      </DialogContent>
    </CustomDialog>
  );
};

FieldOptionsDialog.propTypes = {
  onClose: PropTypes.func,
  show: PropTypes.bool,
};

export default FieldOptionsDialog;
